import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { User, UserRole } from '@nhanho/shared'
import { auth as authApi, UNAUTHORIZED_EVENT } from './api'

interface AuthState {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  /**
   * Cập nhật user trong context sau khi tự sửa hồ sơ (`/account`).
   *
   * Cần vì tên hiển thị nằm ở sidebar (`Shell.tsx`): không đẩy lại thì người vừa đổi tên
   * vẫn thấy tên cũ cho tới lần F5 sau.
   */
  setUser: (u: User) => void
  /** Toàn quyền: thao tác hàng loạt + cấu hình toàn site. */
  isAdmin: boolean
  /** Quản lý nội dung trở lên (admin **hoặc** manager): sản phẩm, danh mục, màu, video. */
  isManager: boolean
}

/**
 * Thang bậc quyền — **admin ⊃ manager ⊃ editor**.
 *
 * ⚠️ Phải khớp `RANK` ở `apps/api/src/middleware/auth.ts`. Bảng này **chỉ để ẩn/hiện**;
 * chặn thật luôn nằm ở API. `Record` đầy đủ (không `Partial`) để thêm vai mới mà quên xếp
 * hạng thì TypeScript gãy lúc build.
 */
const RANK: Record<UserRole, number> = { admin: 3, manager: 2, editor: 1 }

/** `undefined` (chưa đăng nhập / chưa load xong) → luôn `false`, không bao giờ mở nhầm. */
export function roleAtLeast(role: UserRole | undefined, min: UserRole): boolean {
  return role ? RANK[role] >= RANK[min] : false
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Lần load đầu: hỏi server còn phiên không (cookie httpOnly, JS không tự đọc được).
  useEffect(() => {
    authApi
      .me()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  // Bất kỳ request nào bị 401 (phiên hết hạn, đổi mật khẩu ở tab khác…) → đăng xuất tại chỗ.
  useEffect(() => {
    const onUnauthorized = () => setUser(null)
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const r = await authApi.login(email, password)
    setUser(r.user)
  }, [])

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => {})
    setUser(null)
  }, [])

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      setUser,
      isAdmin: roleAtLeast(user?.role, 'admin'),
      isManager: roleAtLeast(user?.role, 'manager'),
    }),
    [user, loading, login, logout],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth phải nằm trong <AuthProvider>')
  return ctx
}
